import React, { useState, useEffect } from 'react';
import AceEditor from 'react-ace';
import axios from 'axios';

// Instead of monokai, we now import the "github" theme (a light theme)
import 'ace-builds/src-noconflict/mode-lisp';
import 'ace-builds/src-noconflict/theme-github';
import 'ace-builds/src-noconflict/ext-language_tools';
import './Interpreter.css';

// Example unicode characters for custom autocompletion
const unicodeCharacters = [
  { caption: 'alpha', value: 'α' },
  { caption: 'beta', value: 'β' },
  // ... any additional definitions
];

function Interpreter() {
  const [code, setCode] = useState('');
  const [output, setOutput] = useState([]);
  const [customOperators, setCustomOperators] = useState([]);

  // 新增：loading 状态和执行中的文字
  const [isLoading, setIsLoading] = useState(false);
  const [loadingText, setLoadingText] = useState('');

  // Load custom operators from localStorage (optional)
  useEffect(() => {
    const savedOperators = localStorage.getItem('customOperators');
    if (savedOperators) {
      setCustomOperators(JSON.parse(savedOperators));
    }
  }, []);

  // Save custom operators to localStorage (optional)
  useEffect(() => {
    localStorage.setItem('customOperators', JSON.stringify(customOperators));
  }, [customOperators]);

  // Add custom completer for AceEditor (e.g., handling unicode characters)
  useEffect(() => {
    const customCompleter = {
      getCompletions: function (editor, session, pos, prefix, callback) {
        if (prefix.length === 0) {
          callback(null, []);
          return;
        }
        const matches = unicodeCharacters.filter((char) =>
            char.caption.toLowerCase().includes(prefix.toLowerCase())
        );
        callback(null, matches.slice(0, 20)); // Limit suggestions to 20
      },
    };
    window.ace.require('ace/ext/language_tools').addCompleter(customCompleter);
  }, []);

  // 新增：在 isLoading 为 true 时，每隔一段时间切换 loadingText
  useEffect(() => {
    let intervalId;
    if (isLoading) {
      let count = 0;
      const states = ['executing', 'executing.', 'executing..', 'executing...'];
      intervalId = setInterval(() => {
        setLoadingText(states[count % states.length]);
        count++;
      }, 500);
    } else {
      setLoadingText('');
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [isLoading]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // 开始执行请求前：设置 isLoading，清空旧的输出
    setIsLoading(true);
    setOutput([]);

    try {
      const response = await axios.post('https://ai-lisp.onrender.com/interpret', {
        code,
        customOperators,
      });
      // 请求成功后，将结果放入 output
      setOutput(response.data.output);
    } catch (error) {
      setOutput([error.response ? error.response.data.error : 'An error occurred']);
    } finally {
      // 不管成功还是失败，都结束 loading
      setIsLoading(false);
    }
  };

  const loadExample = (exampleCode) => {
    setCode(exampleCode);
  };

  // Handlers for custom operators
  const addCustomOperator = () => {
    setCustomOperators([...customOperators, { symbol: '', meaning: '' }]);
  };

  const updateCustomOperator = (index, key, value) => {
    const updatedOperators = customOperators.map((op, idx) =>
        idx === index ? { ...op, [key]: value } : op
    );
    setCustomOperators(updatedOperators);
  };

  const deleteCustomOperator = (index) => {
    const updatedOperators = customOperators.filter((_, idx) => idx !== index);
    setCustomOperators(updatedOperators);
  };

  return (
      <div className="interpreter-container">
        {/* 示例按钮区域 */}
        <div className="example-buttons">
          <button
              onClick={() => loadExample('(define (square x)\n  (* x x))\n(square 5)')}
              className="btn example-btn"
          >
            Example: Square Function
          </button>
          <button
              onClick={() => loadExample('(define x 10)\n(ai "is bigger than 5" x)')}
              className="btn example-btn"
          >
            Example: Bigger Than 5
          </button>
          <button
              onClick={() => loadExample('(+ [four multiplies two] 3)')}
              className="btn example-btn"
          >
            Example: AI Add
          </button>
          <button
              onClick={() => loadExample('(define (int-to-roman num)\n  [ Convert the integer (if (> num 1) num 1)\n    into a Roman numeral string ])\n\n(int-to-roman 4)')}
              className="btn example-btn"
          >
            Example: Integer to Roman
          </button>
        </div>

        {/* 自定义操作符区域（如果需要） */}
        <div className="custom-operators-section">
          {customOperators.map((operator, index) => (
              <div key={index} className="custom-operator-row">
                <AceEditor
                    mode="text"
                    theme="github"
                    name={`operator-symbol-${index}`}
                    onChange={(value) => updateCustomOperator(index, 'symbol', value)}
                    value={operator.symbol}
                    editorProps={{ $blockScrolling: true }}
                    width="60px"
                    fontSize={14}
                    setOptions={{
                      enableBasicAutocompletion: true,
                      enableLiveAutocompletion: true,
                      showLineNumbers: false,
                      showGutter: false,
                      tabSize: 2,
                      maxLines: 1,
                      minLines: 1,
                      wrap: false,
                    }}
                    commands={[
                      {
                        name: 'disableEnter',
                        bindKey: { win: 'Enter', mac: 'Enter' },
                        exec: () => {},
                      },
                    ]}
                    className="operator-symbol-editor"
                />
                <input
                    type="text"
                    placeholder="Meaning"
                    value={operator.meaning}
                    onChange={(e) => updateCustomOperator(index, 'meaning', e.target.value)}
                    className="operator-meaning-input"
                />
                <button
                    onClick={() => deleteCustomOperator(index)}
                    className="btn delete-operator-btn"
                >
                  Delete
                </button>
              </div>
          ))}
        </div>

        {/* 代码编辑+运行区域 */}
        <form onSubmit={handleSubmit}>
          <AceEditor
              mode="lisp"
              theme="github"
              name="code-editor"
              onChange={(value) => setCode(value)}
              value={code}
              editorProps={{ $blockScrolling: true }}
              width="100%"
              height="300px"
              fontSize={14}
              setOptions={{
                enableBasicAutocompletion: true,
                enableLiveAutocompletion: true,
                enableSnippets: true,
                showLineNumbers: true,
                tabSize: 2,
              }}
              className="code-editor"
          />

          <div className="form-buttons">
            <button type="submit" className="btn run-code-btn">
              Run Code
            </button>
          </div>
        </form>

        {/* 输出区域 */}
        <h2>Output:</h2>
        <div className="output-container">
          {isLoading ? (
              // 如果正在loading，就显示动态的“executing...”
              <div>{loadingText}</div>
          ) : (
              // 否则显示真实输出
              output.map((line, index) => <div key={index}>{line}</div>)
          )}
        </div>
      </div>
  );
}

export default Interpreter;
